import React, { useEffect, useMemo, useCallback, useContext, useState, useReducer } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import {
  ApproveAllWithoutDiscrepanciesConfirmationModal,
  CircleIcon,
  ConfirmationModal,
  ContextMenu,
  FlexCenter,
  FlexMiddle,
  Loader,
  Tooltip
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useProject from '@ubeya/shared/hooks/account/useProject';
import { ReactComponent as BaseArrowIcon } from '@ubeya/shared-web/assets/arrow-right-small.svg';
import useToaster from '@ubeya/shared/hooks/useToaster';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { ReactComponent as ArchiveIcon } from '@ubeya/shared-web/assets/archive.svg';
import { TIMESHEET_GROUP_BY_OPTIONS_ENUM } from '@ubeya/shared/constants';
import privileges from '@ubeya/shared-web/privileges';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import { generateRowsToApprove } from 'shared/utils/timesheet';
import ClientTimesheetApprovalSignatureModal from 'shared/components/OrderTimeSheetTable/ClientTimesheetApprovalSignatureModal';
import TimesheetAutoFillModal from '../TimesheetAutoFillModal';
import InvoiceDataModal from 'scheduling/components/ManageBooking/InvoiceDataModal';

const IconsWrapper = styled(FlexMiddle).attrs(() => ({ className: 'row-actions-wrapper' }))`
  gap: 8px;
`;

const LoaderWrapper = styled(FlexCenter)`
  margin-right: 8px;
`;

const MobileExportLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ArrowIcon = styled(BaseArrowIcon)`
  ${({ $isRtl }) => $isRtl && 'transform: rotate(180deg);'};
  ${({ $isOpen }) => $isOpen && 'transform: rotate(90deg);'};
`;

const isUnapprovedRow = (rowData) => {
  const { dummy, actualIn, actualOut, approved, jobTypeId, isExternal, clientApproved, isPto } = rowData;
  const rowNotApproved = isExternal ? !clientApproved : !approved;
  const hasInOut = !dummy && actualIn && actualOut;
  const isDaily = jobTypeId !== 1;

  return rowNotApproved && (isDaily || hasInOut || isPto);
};

const CloseEventHandler = ({ localProjectState, isClientIdExist, isGroupByEvent, hasActionPermission, onClick }) => {
  const { t } = useContext(ConfigContext);

  const tooltipBody = !localProjectState.hoursAllFilled
    ? t('timesheetNotFilledTooltip')
    : t('payrollCloseEventTooltip');

  const showCloseEvent =
    isGroupByEvent &&
    (!localProjectState.projectIsArchived ||
      !localProjectState.projectIsPayrollLock ||
      (!localProjectState.projectIsInvoiceIssued && isClientIdExist));

  const isDisabled = !localProjectState.hoursAllFilled || !hasActionPermission;

  if (!showCloseEvent) return <></>;

  return (
    <Tooltip body={tooltipBody} placement="left">
      <CircleIcon icon={ArchiveIcon} onClick={onClick} isDisabled={isDisabled} />
    </Tooltip>
  );
};

const ACTION_TYPES = {
  AUTO_FILL: 'autoFill',
  APPROVE_TIMESHEETS: 'approveTimesheets'
};

const ActionsAggregateRenderer = ({
  refetchTimesheet,
  mappedSupplierBranches,
  rowData: { subRows },
  columnData: {
    approveAllTimeSheet: approveInternalTimeSheets,
    approveOSTimeSheets,
    autofill,
    autoFillOSTimeSheets,
    groupBy,
    hasActionPermission
  }
}) => {
  const { t, shortDateFormat } = useContext(ConfigContext);
  const { account } = useAccountConfig();
  const { isInvoiceAutoIncrement } = account?.payrollConfig || {};

  const [approveAllWithoutDiscrepanciesAddTolerance, setApproveAllWithoutDiscrepanciesAddTolerance] = useState(false);
  const [showApproveAllWithoutDiscrepanciesConfirm, setShowApproveAllWithoutDiscrepanciesConfirm] = useState(false);

  const myPrivileges = useContext(PrivilegesContext);
  const hasPrivilegeToApproveTimesheets = !!myPrivileges[privileges.APPROVE_TIMESHEET];

  const { addSuccess, addResponseError } = useToaster();

  const { updateProject } = useProject({
    projectId: subRows[0]?.projectId,
    branchId: subRows[0]?.branchId,
    enable: false,
    context: 'ActionsAggregateRenderer'
  });

  const [loading, setLoading] = useState(false);
  const [showInvoiceNumberPrompt, setShowInvoiceNumberPrompt] = useState(false);
  const [autoFillHoursModal, setAutoFillHoursModal] = useState(false);
  const [openApproveSignatureModal, setOpenApproveSignatureModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [localProjectState, setLocalProjectState] = useState({
    projectIsArchived: !!subRows[0]?.projectIsArchived,
    projectIsInvoiceIssued: !!subRows[0]?.projectIsInvoiceIssued,
    projectIsPayrollLock: !!subRows[0]?.projectIsPayrollLock,
    hoursAllFilled: subRows?.every((row) => row.actualIn && row.actualOut),
    hoursAllApproved: subRows?.every((row) => row.approved)
  });

  const [showApproveOptions, toggleShowApproveOptions] = useReducer((prevState) => !prevState, false);

  useEffect(() => {
    setLocalProjectState({
      projectIsArchived: !!subRows[0]?.projectIsArchived,
      projectIsInvoiceIssued: !!subRows[0]?.projectIsInvoiceIssued,
      projectIsPayrollLock: !!subRows[0]?.projectIsPayrollLock,
      hoursAllFilled: subRows?.every((row) => row.actualIn && row.actualOut),
      hoursAllApproved: subRows?.every((row) => row.approved)
    });
  }, [subRows]);

  const { rowsToAutofill, rowsToApprove, rowsToApproveWithoutErrors, totalToApprove } = useMemo(() => {
    const unlockedRows = subRows && subRows.filter((row) => !row.isLocked && !row.isPto);
    const unapprovedRows = subRows && subRows.filter(isUnapprovedRow);

    const { unapprovedWithoutErrorsRows, totalToApprove: totalToApproveInner = 0 } = generateRowsToApprove({
      unapprovedRows,
      approveAllWithoutDiscrepanciesAddTolerance
    });

    if (hasPrivilegeToApproveTimesheets) {
      return {
        rowsToAutofill: unlockedRows,
        rowsToApprove: unapprovedRows,
        rowsToApproveWithoutErrors: unapprovedWithoutErrorsRows,
        totalToApprove: totalToApproveInner
      };
    }

    /* Admins that don't have the permission to approve timesheets:
        - Can't approve internal timesheets, but can approve external timesheets (supplier employees)
        - Can't "Approve all" / approve all without discrepancies for internals, 
        they should be able to approve all + discrepancies for external timesheets ->
         Need to filter out internal employees if you don't have the permission */

    const externalUnapprovedRows = unapprovedRows && unapprovedRows.filter((row) => row?.isExternal);
    const externalUnapprovedWithoutErrorsRows =
      unapprovedWithoutErrorsRows && unapprovedWithoutErrorsRows.filter((row) => row?.isExternal);

    return {
      rowsToAutofill: unlockedRows,
      rowsToApprove: externalUnapprovedRows,
      rowsToApproveWithoutErrors: externalUnapprovedWithoutErrorsRows,
      //check
      totalToApprove: externalUnapprovedWithoutErrorsRows.length
    };
  }, [approveAllWithoutDiscrepanciesAddTolerance, hasPrivilegeToApproveTimesheets, subRows]);

  const isClientIdExist = !!subRows[0]?.clientId;
  const isGroupByEvent = groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.PROJECT && !!subRows[0]?.projectId;

  const onClosePrompt = useCallback(() => setShowPrompt(false), []);
  const onCloseInvoiceNumberPrompt = useCallback(() => setShowInvoiceNumberPrompt(false), []);
  const onCloseAutoFillHoursModal = useCallback(() => setAutoFillHoursModal(false), []);
  const onCloseApproveModal = useCallback(() => setOpenApproveSignatureModal(false), []);

  const getTimesheetData = useCallback((rows, action) => {
    const noDummyRows = rows.filter(({ dummy }) => !dummy);

    return noDummyRows.reduce(
      (output, current) => {
        const { timesheetId, isExternal } = current;
        if (isExternal) {
          output.externalTimesheetIds.push(timesheetId);
        } else if (action === ACTION_TYPES.AUTO_FILL) output.internalRows.push(current);
        else if (action === ACTION_TYPES.APPROVE_TIMESHEETS) output.ids.push(timesheetId);
        return output;
      },
      { externalTimesheetIds: [], ids: [], internalRows: [] }
    );
  }, []);

  const handleAutoFill = useCallback(
    async ({ rows = [], override = true }) => {
      const { externalTimesheetIds = [], internalRows = [] } = getTimesheetData(rows, ACTION_TYPES.AUTO_FILL);

      const autoFillResults = await Promise.allSettled([
        ...(internalRows.length ? [autofill({ rows: internalRows, override })] : []),
        ...(externalTimesheetIds.length ? [autoFillOSTimeSheets?.({ externalTimesheetIds, override })] : [])
      ]);

      const isAllAutoFillFailed = autoFillResults.every(({ status }) => status === 'rejected');

      if (isAllAutoFillFailed) {
        throw autoFillResults[0].reason;
      }
    },
    [getTimesheetData, autofill, autoFillOSTimeSheets]
  );

  const handleApproveAllTimesheets = useCallback(
    ({ ids, externalTimesheetIds, signature }) => {
      toggleShowApproveOptions();
      approveInternalTimeSheets({ ids });

      if (externalTimesheetIds.length > 0) {
        approveOSTimeSheets({ externalTimesheetIds, signature });
      }

      addSuccess(t('successApproveTimesheet'));
    },
    [addSuccess, approveInternalTimeSheets, approveOSTimeSheets, t]
  );

  const onApproveWithSignature = useCallback(
    ({ signature }) => {
      const { ids, externalTimesheetIds } = openApproveSignatureModal;
      handleApproveAllTimesheets({
        ids,
        externalTimesheetIds,
        signature
      });
    },
    [handleApproveAllTimesheets, openApproveSignatureModal]
  );

  const onApproveTimesheets = useCallback(
    (rows) => {
      const { ids, externalTimesheetIds } = getTimesheetData(rows, ACTION_TYPES.APPROVE_TIMESHEETS);

      const clientSignatureRequired = rows.some(
        ({ supplierData: { branchId } = {} }) =>
          mappedSupplierBranches?.[branchId]?.timesheetConfig?.requireClientTimesheetSignoff
      );

      if (clientSignatureRequired) return setOpenApproveSignatureModal({ ids, externalTimesheetIds });

      handleApproveAllTimesheets({
        ids,
        externalTimesheetIds
      });
    },
    [getTimesheetData, handleApproveAllTimesheets, mappedSupplierBranches]
  );

  const handleUpdateProject = useCallback(
    async (payload, newProjectState, toasterText) => {
      try {
        setLoading(true);

        await updateProject(payload);
        await refetchTimesheet();

        setLocalProjectState((prev) => ({ ...prev, ...newProjectState }));

        if (Array.isArray(toasterText)) toasterText.forEach((text) => addSuccess(t(text)));
        else addSuccess(t(toasterText));
      } catch (err) {
        addResponseError(err);
      } finally {
        setLoading(false);
      }
    },
    [t, refetchTimesheet, addResponseError, addSuccess, updateProject]
  );

  const handleCloseEvent = useCallback(
    (invoiceNumber) => {
      const payload = {};
      const newProjectState = {};
      const toasterText = [];

      if (!localProjectState.projectIsArchived) {
        payload.isArchived = true;
        newProjectState.projectIsArchived = true;
        toasterText.push('eventArchivedMessage');
      }
      if (!localProjectState.projectIsPayrollLock) {
        payload.isPayrollLock = true;
        newProjectState.projectIsPayrollLock = true;
        toasterText.push('payrollLockedSuccessfully');
      }
      if (!localProjectState.projectIsInvoiceIssued && isClientIdExist) {
        payload.isInvoiceIssued = true;
        payload.invoiceNumber = invoiceNumber && Number(invoiceNumber);
        newProjectState.projectIsInvoiceIssued = true;
        toasterText.push('invoiceIssuedSuccessfully');
      }

      return handleUpdateProject(payload, newProjectState, toasterText);
    },
    [
      handleUpdateProject,
      isClientIdExist,
      localProjectState.projectIsArchived,
      localProjectState.projectIsInvoiceIssued,
      localProjectState.projectIsPayrollLock
    ]
  );

  const handleLockInvoice = useCallback(
    (invoiceNumber) =>
      handleUpdateProject(
        {
          isInvoiceIssued: true,
          ...(isInvoiceAutoIncrement && { isPayrollLock: true }),
          invoiceNumber: invoiceNumber && Number(invoiceNumber)
        },
        { projectIsInvoiceIssued: true, projectIsPayrollLock: true },
        'invoiceIssuedSuccessfully'
      ),
    [handleUpdateProject, isInvoiceAutoIncrement]
  );

  const closeEventOrLockInvoice = useCallback(
    (isArchive, invoiceNumber) => {
      if (isArchive) {
        handleCloseEvent(invoiceNumber);
        return;
      }
      handleLockInvoice(invoiceNumber);
    },
    [handleCloseEvent, handleLockInvoice]
  );

  const onConfirmLockInvoicePrompt = useCallback(
    (isArchive, invoiceNumber = undefined) => {
      onCloseInvoiceNumberPrompt();
      closeEventOrLockInvoice(isArchive, invoiceNumber);
    },
    [closeEventOrLockInvoice, onCloseInvoiceNumberPrompt]
  );

  const handleShowLockInvoicePrompt = useCallback(
    ({ doArchive }) => {
      if (isInvoiceAutoIncrement) {
        setShowInvoiceNumberPrompt({
          onConfirm: (invoiceNumber = undefined) => onConfirmLockInvoicePrompt(doArchive, invoiceNumber),
          onClose: onCloseInvoiceNumberPrompt
        });
        return;
      }
      closeEventOrLockInvoice(doArchive);
    },
    [isInvoiceAutoIncrement, closeEventOrLockInvoice, onCloseInvoiceNumberPrompt, onConfirmLockInvoicePrompt]
  );

  const handleShowCloseEventPrompt = useCallback(() => {
    setShowPrompt({
      title: t('areYouSure'),
      body: (
        <>
          {t('groupByEventArchivePromptBody')}
          <br />
          {t('groupByEventArchivePromptSecondary')}
        </>
      ),
      onConfirm: () => {
        onClosePrompt();
        handleShowLockInvoicePrompt({ doArchive: true });
      }
    });
  }, [t, onClosePrompt, handleShowLockInvoicePrompt]);

  const onConfirmApproveHoursPrompt = useCallback(() => {
    onApproveTimesheets(rowsToApprove);
    handleShowCloseEventPrompt();
  }, [onApproveTimesheets, handleShowCloseEventPrompt, rowsToApprove]);

  const handleShowApproveHoursPrompt = useCallback(() => {
    setShowPrompt({
      title: t('groupByEventApproveAllPrompt'),
      body: t('groupByEventApproveAllBody'),
      onConfirm: onConfirmApproveHoursPrompt,
      onClose: handleShowCloseEventPrompt,
      confirmText: t('approveAll')
    });
  }, [t, onConfirmApproveHoursPrompt, handleShowCloseEventPrompt]);

  // Documentation:
  // 1. Click close event icon
  // 2. if event hours are approved then show close event prompt
  // 3. if not then show approve hours propmt -> no matter the choice, show close event prompt after.
  // 4. Close event prompt
  // 5. If confirmed show invoice modal if account is autoIncrement
  // 6. Close invoice modal
  // 7. If confirmed run handleUpdateProject

  const onCloseEventClick = useCallback(() => {
    if (!localProjectState.hoursAllApproved) {
      return handleShowApproveHoursPrompt();
    }
    handleShowCloseEventPrompt();
  }, [localProjectState.hoursAllApproved, handleShowCloseEventPrompt, handleShowApproveHoursPrompt]);

  const onConfirmArchiveProjectPrompt = useCallback(() => {
    handleUpdateProject({ isArchived: true }, { projectIsArchived: true }, 'eventArchivedMessage');
    onClosePrompt();
  }, [handleUpdateProject, onClosePrompt]);

  const handleShowArchiveProjectPrompt = useCallback(() => {
    const { projectStartDate, projectEndDate, isMultiDay } = subRows[0];
    const body = isMultiDay
      ? t('wouldYouLikeToArchiveThisEventMultiDay', {
          startDate: moment(projectStartDate).format(shortDateFormat),
          endDate: moment(projectEndDate).format(shortDateFormat)
        })
      : t('wouldYouLikeToArchiveThisEvent');
    setShowPrompt({
      title: t('archiveThisEvent'),
      body,
      onConfirm: onConfirmArchiveProjectPrompt
    });
  }, [t, shortDateFormat, subRows, onConfirmArchiveProjectPrompt]);

  const visibleOptions = useMemo(
    () => ({
      archive: isGroupByEvent && !localProjectState.projectIsArchived,
      unArchive: isGroupByEvent && localProjectState.projectIsArchived,
      lockPayroll: isGroupByEvent && !localProjectState.projectIsPayrollLock,
      unlockPayroll: isGroupByEvent && localProjectState.projectIsPayrollLock,
      lockInvoice: isGroupByEvent && isClientIdExist && !localProjectState.projectIsInvoiceIssued,
      unlockInvoice: isGroupByEvent && isClientIdExist && localProjectState.projectIsInvoiceIssued
    }),
    [
      isGroupByEvent,
      isClientIdExist,
      localProjectState.projectIsArchived,
      localProjectState.projectIsPayrollLock,
      localProjectState.projectIsInvoiceIssued
    ]
  );

  const approveOptions = useMemo(
    () => [
      {
        label: t('approveAll'),
        confirm: true,
        confirmTitle: t('approveAll'),
        confirmBody: t('areYouSureApproveAll', { shifts: rowsToApprove.length }),
        confirmText: 'yes',
        confirmCancelButton: true,
        confirmPortal: true,
        onClick: () => onApproveTimesheets(rowsToApprove),
        disabled: !hasPrivilegeToApproveTimesheets || !rowsToApprove.length
      },
      {
        label: t('approveAllWithoutDiscrepancies'),
        customTooltip: totalToApprove === 0 ? t('noTimesheetsToapproveWithoutDiscrepanciesTooltip') : null,
        onClick: () => setShowApproveAllWithoutDiscrepanciesConfirm(true),
        disabled: !hasPrivilegeToApproveTimesheets || totalToApprove === 0
      }
    ],
    [hasPrivilegeToApproveTimesheets, onApproveTimesheets, rowsToApprove, t, totalToApprove]
  );

  const options = useMemo(
    () => [
      {
        label: t('archiveEvent'),
        onClick: handleShowArchiveProjectPrompt,
        visible: visibleOptions.archive
      },
      {
        label: t('unarchiveEvent'),
        onClick: () =>
          handleUpdateProject({ isArchived: false }, { projectIsArchived: false }, 'eventUnarchivedMessage'),
        visible: visibleOptions.unArchive
      },
      {
        label: t('issueInvoice'),
        onClick: () => handleShowLockInvoicePrompt({ doArchive: false }),
        visible: visibleOptions.lockInvoice,
        disabled: !hasActionPermission
      },
      {
        label: t('unlockInvoice'),
        onClick: () =>
          handleUpdateProject(
            { isInvoiceIssued: false },
            { projectIsInvoiceIssued: false },
            'invoiceUnlockedSuccessfully'
          ),
        visible: visibleOptions.unlockInvoice
      },
      {
        label: t('autoFillHours'),
        onClick: () => setAutoFillHoursModal({ rows: rowsToAutofill }),
        customTooltip: !rowsToAutofill.length ? t('cantUpdateLockedShiftsTooltip') : null,
        disabled: !rowsToAutofill.length || !hasActionPermission
      },
      {
        label: (
          <MobileExportLabel>
            {t('approve')} <ArrowIcon $isOpen={showApproveOptions} />
          </MobileExportLabel>
        ),
        onClick: toggleShowApproveOptions,
        closeOnClick: false,
        disabled: !rowsToApprove.length,
        customTooltip: !rowsToApprove.length ? t('noTimesheetsToapproveTooltip') : null
      },
      ...(rowsToApprove.length > 0
        ? approveOptions.map((option) => ({ ...option, css: { marginLeft: 15 }, visible: showApproveOptions }))
        : [])
    ],
    [
      t,
      hasActionPermission,
      handleShowArchiveProjectPrompt,
      visibleOptions.archive,
      visibleOptions.unArchive,
      visibleOptions.lockInvoice,
      visibleOptions.unlockInvoice,
      rowsToAutofill,
      showApproveOptions,
      rowsToApprove.length,
      approveOptions,
      handleUpdateProject,
      handleShowLockInvoicePrompt
    ]
  );

  return loading ? (
    <LoaderWrapper>
      <Loader size="small" />
    </LoaderWrapper>
  ) : (
    <IconsWrapper>
      <CloseEventHandler
        localProjectState={localProjectState}
        isClientIdExist={isClientIdExist}
        isGroupByEvent={isGroupByEvent}
        hasActionPermission={hasActionPermission}
        onClick={onCloseEventClick}
      />

      <ContextMenu options={options} usePortal />

      {showPrompt && (
        <ConfirmationModal
          title={showPrompt.title}
          onConfirm={showPrompt.onConfirm}
          onClose={showPrompt.onClose || onClosePrompt}
          confirmText={showPrompt.confirmText || t('yes')}
          cancelText={showPrompt.cancelText || t('no')}
          usePortal>
          {showPrompt.body}
        </ConfirmationModal>
      )}

      {showApproveAllWithoutDiscrepanciesConfirm && (
        <ApproveAllWithoutDiscrepanciesConfirmationModal
          approveAllWithoutDiscrepanciesAddTolerance={approveAllWithoutDiscrepanciesAddTolerance}
          setApproveAllWithoutDiscrepanciesAddTolerance={setApproveAllWithoutDiscrepanciesAddTolerance}
          rowsToApproveWithoutErrors={rowsToApproveWithoutErrors}
          onClose={() => setShowApproveAllWithoutDiscrepanciesConfirm(false)}
          onConfirm={onApproveTimesheets}
        />
      )}

      {showInvoiceNumberPrompt && (
        <InvoiceDataModal
          onConfirm={showInvoiceNumberPrompt.onConfirm}
          onClose={showInvoiceNumberPrompt.onClose}
          usePortal
        />
      )}

      {autoFillHoursModal && (
        <TimesheetAutoFillModal
          autofill={handleAutoFill}
          data={autoFillHoursModal}
          onClose={onCloseAutoFillHoursModal}
        />
      )}

      {openApproveSignatureModal && (
        <ClientTimesheetApprovalSignatureModal
          handleClientApprove={onApproveWithSignature}
          onClose={onCloseApproveModal}
        />
      )}
    </IconsWrapper>
  );
};

export default ActionsAggregateRenderer;
